import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../store/store';
import {
  clearClinicSettingsDetail,
  getClinicSettingsById,
  getSchedule,
} from '../../../store/reducers/ClinicSettingsDetailSlice';
import { getSipAccounts } from '../../../store/reducers/SIPAccountSlice';
import { getCallPools } from '../../../store/reducers/CallPoolSlice';
import {
  clearCatalog,
  loadSpecializationsAndDoctors,
} from '../../../store/reducers/SpecializationDoctorCatalogSlice';
import type { ClinicSettingsItem } from '../../../models/Settings/ClinicSettings';
import { DEFAULT_SCHEDULE, weeklyScheduleToLocal } from './schedule';
import type { ScheduleDay } from './schedule';
import type { ScenarioType } from './callSettings';
import type { ClinicSettingsDomain } from './settingsDomainTypes';
import type { useScenarioCallSchedule } from './useScenarioCallSchedule';
import { getScenarioValues } from './scenarioSettingsRequests';

interface ClinicSettingsLoadingDomain extends ClinicSettingsDomain {
  readonly loadCallSchedule: ReturnType<
    typeof useScenarioCallSchedule
  >['loadCallSchedule'];
}

export const useClinicSettingsLoading = (
  clinicId: string | undefined,
  item: ClinicSettingsItem | null,
  domain: ClinicSettingsLoadingDomain,
) => {
  const dispatch = useAppDispatch();
  const {
    scenario,
    doctors,
    phone,
    schedule,
    dirty,
    loadCallSchedule,
    requests,
  } = domain;
  const [sipAccountId, setSipAccountId] = useState<string | null>(null);
  const [callPoolId, setCallPoolId] = useState<string | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);

  const fetchSlotSettings = () => {
    requests
      .loadSlotSettings()
      .then((data) => {
        if (!data) return;
        const values = {
          ...getScenarioValues('slot', data),
          callsScheduleId: data.callsScheduleId ?? null,
          callSchedule: null,
          callHolidays: null,
        };
        const excludedSpecializationIds =
          data.callsExcludedSpecializationIds ?? [];
        const excludedDoctorIds = data.callsExcludedDoctorIds ?? [];
        scenario.applyLoadedValues('slot', values);
        doctors.setExcludedSpecializationIds(excludedSpecializationIds);
        doctors.setExcludedDoctorIds(excludedDoctorIds);
        scenario.setServerSnapshot('slot', values);
        dirty.setSlotSnapshot({
          callsMaxTries: values.callsMaxTries,
          callsIntervalMinutes: values.callsIntervalMinutes,
          callsDisable: values.callsDisable,
          excludedSpecializationIds: [...excludedSpecializationIds],
          excludedDoctorIds: [...excludedDoctorIds],
        });
        loadCallSchedule(data.callsScheduleId, 'slot');
      })
      // Rejected thunks expose their error through the detail status.
      .catch(() => undefined);
  };

  const fetchScenarioMeta = (type: ScenarioType) => {
    requests
      .loadScenarioSettings(type)
      .then((data) => {
        if (!data) return;
        const values = {
          ...getScenarioValues(type, data),
          callsScheduleId: data.callsScheduleId ?? null,
        };
        scenario.cacheValues(type, values);
        scenario.setServerSnapshot(type, values);
      })
      // Rejected thunks expose their error through the detail status.
      .catch(() => undefined);
  };

  useEffect(() => {
    if (clinicId) {
      dispatch(getClinicSettingsById(clinicId));
      fetchSlotSettings();
      fetchScenarioMeta('notification');
      fetchScenarioMeta('dispensarization');
      fetchScenarioMeta('form');
      dispatch(loadSpecializationsAndDoctors([clinicId]));
    }
    dispatch(getSipAccounts());
    dispatch(getCallPools());
    return () => {
      dispatch(clearClinicSettingsDetail());
      dispatch(clearCatalog());
    };
  }, [clinicId]);

  useEffect(() => {
    if (!item) return;
    const operatorPhone =
      item.operatorPhone === '0' ? '' : (item.operatorPhone ?? '');
    const sipId = item.sipAccount?.id ?? null;
    const poolId = item.callPoolId ?? null;
    phone.setOperatorPhone(operatorPhone);
    setSipAccountId(sipId);
    setCallPoolId(poolId);

    const buildClinicSnapshot = (days: ScheduleDay[], holidays: string[]) => {
      dirty.setClinicSnapshot({
        operatorPhone,
        sipAccountId: sipId,
        callPoolId: poolId,
        schedule: days.map((day) => ({ ...day })),
        holidays: [...holidays],
        callSchedule: DEFAULT_SCHEDULE.map((day) => ({ ...day })),
        callHolidays: [],
      });
    };
    if (!item.operatorScheduleId) {
      buildClinicSnapshot(
        DEFAULT_SCHEDULE.map((day) => ({ ...day })),
        [],
      );
      setScheduleLoading(false);
      return;
    }
    setScheduleLoading(true);
    dispatch(getSchedule(item.operatorScheduleId))
      .unwrap()
      .then((dao) => {
        const values = weeklyScheduleToLocal(dao);
        schedule.setSchedule(values.schedule);
        schedule.setHolidays(values.holidays);
        buildClinicSnapshot(values.schedule, values.holidays);
      })
      .catch(() => {
        buildClinicSnapshot(
          DEFAULT_SCHEDULE.map((day) => ({ ...day })),
          [],
        );
      })
      .finally(() => setScheduleLoading(false));
  }, [item]);

  return {
    sipAccountId,
    setSipAccountId,
    callPoolId,
    setCallPoolId,
    scheduleLoading,
  };
};
